import {
  type ComponentType,
  type HTMLAttributes,
  type ComponentPropsWithoutRef,
  type ForwardRefExoticComponent,
  type SVGProps,
  type RefAttributes,
  forwardRef,
} from "react"

export const iconSizes = [
  "xs",
  "sm",
  "md",
  "lg",
  "xl",
  "2xl",
  "3xl",
  "4xl",
] as const

export type IconSize = (typeof iconSizes)[number]

export type IconProps = ComponentPropsWithoutRef<"svg"> & {
  /**
   * Indicates the size of the `<Icon />`. Arbitrary sizes are not allowed so that
   * icons are always given one of a narrow set of sizes that fit within
   * the design system
   *
   * @default "sm"
   */
  "data-size"?: IconSize
}

export type Icon = ForwardRefExoticComponent<
  Omit<SVGProps<SVGSVGElement>, "ref"> &
    IconProps &
    RefAttributes<SVGSVGElement>
>

export type BrandedIcon<Brand extends string> = Icon & { brand: Brand }

export type OutlineIcon = BrandedIcon<"outline">

export type SolidIcon = BrandedIcon<"solid">

export type Logo = BrandedIcon<"logo">

function createSvgComponent<Brand extends "outline" | "solid" | "logo">(
  SVG: ComponentType<HTMLAttributes<SVGSVGElement>>,
  label: string,
  initialClassName: string,
  brand: Brand,
) {
  const SvgComponent = forwardRef<SVGSVGElement, IconProps>(
    ({ children, className, ...props }, ref) => {
      return (
        <SVG
          // I don't know how to type this correctly
          // @ts-expect-error
          ref={ref}
          className={`${initialClassName} ${className ?? ""}`}
          {...props}
        />
      )
    },
  )

  ;(SvgComponent as BrandedIcon<Brand>).brand = brand
  SvgComponent.displayName = label

  return SvgComponent as BrandedIcon<Brand>
}

export function createOutlineIcon(
  SVG: ComponentType<HTMLAttributes<SVGSVGElement>>,
  label: string,
) {
  return createSvgComponent(SVG, label, "icon", "outline")
}

export function createSolidIcon(
  SVG: ComponentType<HTMLAttributes<SVGSVGElement>>,
  label: string,
) {
  return createSvgComponent(SVG, label, "icon", "solid")
}

export function createLogo(
  SVG: ComponentType<HTMLAttributes<SVGSVGElement>>,
  label: string,
) {
  return createSvgComponent(SVG, label, "icon no-fill", "logo")
}

export function createLogoAlt(
  SVG: ComponentType<HTMLAttributes<SVGSVGElement>>,
  label: string,
) {
  return createSvgComponent(SVG, label, "icon logo-alt", "logo")
}
